import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Check, ArrowLeft } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import MobileNav from '../components/MobileNav';
import { SHELL_BG } from '../components/AppShell';
import UserProfileMenu from '../components/UserProfileMenu';
import NotFound from './NotFound';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from '@/utils/env';
import { getFreshToken } from '@/utils/auth';
import { showAlert } from '@/components/AppDialog';
import {
  REQUEST_KIND_BY_ID, REQUEST_KIND_BY_SLUG, STATUS_LABEL, normStatus,
  type RequestKind, type RequestKindId,
} from '@/config/serviceRequests';

/* The one client-facing request form, rendered for whichever kind it's given —
   consultation, tax & corporate filings, corporate services, external audit.
   Every kind posts to the same endpoint and lands in the same admin queue
   (/admin/requests), which is why they share a page instead of each getting
   their own. Copy, topics and accent come from @/config/serviceRequests. */

type SentRequest = {
  id: string; kind?: string; topic?: string; message?: string;
  phone?: string; status?: string; requestedAt?: string;
};

const fmt = (s?: string) => {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return s; }
};

const STATUS_STYLE: Record<string, string> = {
  new:       'bg-amber-50 text-amber-700',
  contacted: 'bg-blue-50 text-blue-700',
  scheduled: 'bg-green-50 text-green-700',
  done:      'bg-slate-100 text-slate-500',
  cancelled: 'bg-slate-100 text-slate-400',
};

export default function ServiceRequest({ kind: kindProp }: { kind?: RequestKindId }) {
  const { kind: kindSlug } = useParams();
  const kind: RequestKind | undefined = kindProp
    ? REQUEST_KIND_BY_ID[kindProp]
    : REQUEST_KIND_BY_SLUG[kindSlug || ''];

  // An unknown /request/:kind is a 404, not an empty form.
  if (!kind) return <NotFound />;
  return <RequestForm kind={kind} />;
}

function RequestForm({ kind }: { kind: RequestKind }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [params] = useSearchParams();
  const Icon = kind.icon;

  const [topic, setTopic] = useState(kind.topics[0] || '');
  const [message, setMessage] = useState('');
  const [phone, setPhone] = useState('');
  const [existing, setExisting] = useState<SentRequest[]>([]);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  // Reset when the route switches from one kind to another (the component is reused).
  useEffect(() => {
    setTopic(kind.topics[0] || '');
    setMessage(''); setPhone(''); setDone(false);
  }, [kind.id]);

  useEffect(() => {
    const preset = params.get('topic');
    if (preset && kind.topics.includes(preset)) setTopic(preset);
  }, [params, kind]);

  // The client's own history, so they can see what they've already asked for.
  const loadMine = async () => {
    try {
      const token = await getFreshToken();
      if (!token) return;
      const resp = await fetch(`${API_BASE_URL}/auth/service-requests/mine`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) return;
      const j = await resp.json();
      if (Array.isArray(j.requests)) setExisting(j.requests);
    } catch { /* history is a nicety — never block the form on it */ }
  };
  useEffect(() => { loadMine(); }, []);

  const submit = async () => {
    const u = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
    if (!u?.uid) { navigate('/login'); return; }
    setSaving(true);
    try {
      const token = await getFreshToken();
      const resp = await fetch(`${API_BASE_URL}/auth/service-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          kind: kind.id,
          topic,
          message: message.trim(),
          phone: phone.trim(),
          name: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
          email: u.email || '',
          company: u.companyName || '',
        }),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok || !j.success) throw new Error(j.error || 'Failed to send');
      setDone(true);
      loadMine();
    } catch {
      showAlert('Could not send your request. Please try again.');
    } finally { setSaving(false); }
  };

  const mine = existing.filter(r => (r.kind || 'consultation') === kind.id);
  const inputCls = 'w-full px-3.5 py-2.5 rounded-xl border border-gray-200 text-sm acc-ring focus:outline-none bg-white';

  return (
    <div className="flex min-h-screen" style={{ background: SHELL_BG }}>
      <Sidebar />
      <main className="flex-1 md:ms-64">
        <MobileNav onLogout={logout} />

        <header className="hidden md:flex items-center justify-between px-8 py-5 bg-white border-b border-gray-200">
          <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Icon className="w-5 h-5" style={{ color: kind.accent }} /> {kind.title}
          </h1>
          <UserProfileMenu />
        </header>

        <div className="max-w-2xl mx-auto px-4 md:px-6 py-8">
          <button onClick={() => navigate('/dashboard')} className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-800 mb-5">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </button>

          {done ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
              <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="text-xl font-black text-gray-900 mb-2">Request sent</h2>
              <p className="text-sm text-gray-600 mb-6 leading-relaxed max-w-sm mx-auto">
                Thanks, our team has your request for <b>{topic}</b> and {kind.promise}.
              </p>
              <div className="flex gap-3 justify-center flex-wrap">
                <button onClick={() => navigate('/dashboard')} className="rounded-xl px-6 py-2.5 text-sm font-bold text-white transition" style={{ background: kind.accent }}>Go to dashboard</button>
                <button onClick={() => { setDone(false); setMessage(''); }} className="rounded-xl border border-gray-200 bg-white px-6 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50 transition">Request another</button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-7">
              <div className="w-10 h-10 rounded-xl grid place-items-center mb-3" style={{ background: `${kind.accent}14` }}>
                <Icon className="w-5 h-5" style={{ color: kind.accent }} />
              </div>
              <h2 className="text-xl font-black text-gray-900 mb-1">{kind.title}</h2>
              <p className="text-sm text-gray-500 mb-6">{kind.blurb}</p>

              <label className="block text-xs font-bold text-gray-600 mb-1.5">What's it about?</label>
              <select value={topic} onChange={e => setTopic(e.target.value)} className={`${inputCls} mb-4`}>
                {kind.topics.map(t => <option key={t} value={t}>{t}</option>)}
              </select>

              <label className="block text-xs font-bold text-gray-600 mb-1.5">Anything we should know? <span className="font-medium text-gray-400">(optional)</span></label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} placeholder="A sentence or two about what you need…" className={`${inputCls} mb-4 resize-y`} />

              <label className="block text-xs font-bold text-gray-600 mb-1.5">Best number to reach you <span className="font-medium text-gray-400">(optional)</span></label>
              <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+971 …" className={`${inputCls} mb-6`} />

              <button onClick={submit} disabled={saving} className="w-full rounded-xl py-3 text-sm font-bold text-white transition shadow-sm disabled:opacity-60" style={{ background: kind.accent }}>
                {saving ? 'Sending…' : kind.cta}
              </button>
            </div>
          )}

          {mine.length > 0 && (
            <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 text-sm font-bold text-gray-900">Your requests</div>
              <div className="divide-y divide-slate-100">
                {mine.map(r => {
                  const s = normStatus(r.status);
                  return (
                    <div key={r.id} className="px-6 py-3.5 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-800 truncate">{r.topic || kind.label}</div>
                        <div className="text-xs text-gray-400">{fmt(r.requestedAt)}</div>
                      </div>
                      <span className={`shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_STYLE[s]}`}>
                        {s === 'new' ? 'Requested' : STATUS_LABEL[s]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
